// İSG Portal — Taşeron/Yüklenici Yönetimi (Contractors — tenant-scoped) API çağrıları
// + tipleri. **Ana firma tarafı** (§13.1–13.5): taşeron CRUD + uygunluk belgeleri +
// personel + saha erişimi + davet. Supplier self-service (§13.6) AYRI yetki modülüdür
// (`supplier-contractor`) ve web-supplier'a aittir — bu istemcide YOKTUR.
//
// KAYNAK: api-contract.md §13 (Taşeron/Yüklenici). Backend kanonu; burada **varsayım
// YOK** — endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap
// `value`; Create → `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`)
// doğrudan sözleşmeden türetilmiştir. Serileştirme camelCase'dir (§1).
//
// **Tenant izolasyonu (§4b/§13).** Tüm uçlar çağıranın firmasına (token `CompanyId`)
// kapanır; istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın
// taşeronu/belgesi/personeli hiçbir koşulda görünmez (yok gibi `404 CONTRACTOR_NOT_FOUND`).
// Silme **soft-delete**'tir.
//
// **Uygunluk belgesi = §8 Storage link'i.** Belge dosyası önce §8 akışıyla yüklenir
// (documents.ts: `uploadDocument` → presigned PUT → confirm → `documentUuid`), sonra
// bu modüle `linkContractorDocument` ile linklenir. Bu modül dosya taşımaz; yalnız
// link + tür + geçerlilik. İndirme yine §8 `documentsApi.getDocument(uuid)` iledir.
//
// **Türetilmiş uygunluk (saklanmaz).** `validityStatus` `validUntil` + o anki UTC'den,
// `complianceStatus.overall` belgelerin en kötüsünden türetilir (§13). İstemci bu
// değerleri yalnız gösterir (rozet), yeniden hesaplamaz.

import { api } from './client';
import type { Empty } from './envelope';
import { isApiError } from './errors';

/** Tehlike sınıfı — sayısal wire (§13; 1=Az / 2=Tehlikeli / 3=Çok Tehlikeli). */
export type HazardClass = 1 | 2 | 3;

/** Taşeron durumu — wire değerleri PascalCase (§13 `ContractorDto.status`). */
export type ContractorStatus = 'Active' | 'Suspended' | 'Inactive';

/**
 * Uygunluk durumu — belge (`validityStatus`) ve taşeron özeti (`complianceStatus.overall`)
 * için ortak wire değerleri PascalCase (§13). Öncelik en kötüden iyiye:
 * `Expired > Expiring > Missing > Valid`.
 */
export type ComplianceStatus = 'Valid' | 'Expiring' | 'Expired' | 'Missing';

/**
 * Taşeron uygunluk özeti — api-contract §13 (`ContractorComplianceDto`). Belgelerin
 * durumlarından **türetilir** (saklanmaz). `isCompliant = overall === 'Valid'`.
 */
export interface ContractorComplianceDto {
  overall: ComplianceStatus;
  isCompliant: boolean;
  documentCount: number;
  expiringCount: number;
  expiredCount: number;
}

/**
 * Taşeron/yüklenici firma — api-contract §13 (`ContractorDto`). Sıralı iç `Id` istemciye
 * **sızmaz** (yalnız `uuid`). Sözleşme tarihleri `DateOnly` (yyyy-MM-dd, §4); `createdAt`
 * UTC ISO (§4). `hazardClass` sayısal, `status`/`complianceStatus.overall` PascalCase.
 */
export interface ContractorDto {
  uuid: string;
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  isgKatipNo: string | null;
  hazardClass: HazardClass;
  phone: string | null;
  email: string | null;
  address: string | null;
  /** Sözleşme başlangıcı — DateOnly (yyyy-MM-dd, §4). */
  contractStart: string | null;
  /** Sözleşme bitişi — DateOnly (yyyy-MM-dd, §4). */
  contractEnd: string | null;
  status: ContractorStatus;
  complianceStatus: ContractorComplianceDto;
  createdAt: string | null;
}

/**
 * Uygunluk belgesi linki — api-contract §13.2 (`ContractorDocumentDto`). Her kayıt §8
 * Storage'daki confirm edilmiş bir dosyaya link'tir; `fileName` §8'den çözülür (indirme
 * §8 `documentsApi.getDocument`). `validityStatus` `validUntil` + UTC'den türetilir.
 */
export interface ContractorDocumentDto {
  uuid: string;
  /** `company` entity'sinin §27 kayıt kodu (PascalCase; pasif olabilir; etiket §27 kaydından). */
  type: string;
  documentUuid: string;
  /** §8 dokümanından çözülen özgün dosya adı (indirme için). */
  fileName: string | null;
  /** Geçerlilik başlangıcı — DateTime (UTC ISO 8601, §4). Gün göstermek için kırpılmalıdır. */
  validFrom: string | null;
  /**
   * Geçerlilik bitişi — DateTime (UTC ISO 8601, §4); DateOnly **değildir**. Bir gün değeri
   * (`2026-08-08`) gönderilse bile `2026-08-08T00:00:00.000Z` olarak geri okunur, yani gün
   * göstermek isteyen ekran değeri kendisi kırpar. Türetilmiş durumun kaynağı.
   */
  validUntil: string | null;
  validityStatus: ComplianceStatus;
  note: string | null;
  createdAt: string | null;
  /** GL #7 §4.2: bu dosyayı bu (entity, type) olarak doğrulayan doc-check sürümü; `null` = AI doğrulamadı (elle yükleme vb.). Yalnız sunucu yazar. */
  recognitionRelease: number | null;
}

/**
 * Personel satır türü — api-contract §13.3. Satır **aktive personel** (`SupplierUser`) mi
 * yoksa **bekleyen davet** (`GuestInvitation`) mi olduğunu ayırır; satır aksiyonları buna
 * göre değişir (aktive → düzenle/sil; davet → iptal).
 */
export type ContractorPersonnelKind = 'Personnel' | 'Invitation';

/**
 * Personel/davet durumu — api-contract §13.3 (`ContractorPersonnelDto.status`, PascalCase
 * wire). Aktive personel: `Active`|`Inactive`|`NoLogin` (`NoLogin` = henüz giriş bilgisi yok —
 * `IsActive`'den **bağımsız** türetilir). Davet satırları yalnız düz davettir ve
 * `Pending` (TTL içinde) | `Expired` (TTL geçti) olur — GL6 §4.6: `Revoked` satırlar artık dönmez.
 */
export type ContractorPersonnelStatus = 'Active' | 'Inactive' | 'NoLogin' | 'Pending' | 'Expired';

/**
 * Erişim seviyesi — api-contract §13.3 (`accessLevel`, wire değerleri Türkçe/PascalCase).
 * `Personeli` = varsayılan (mobil, kendi verisi; web portalında `403`); `Yetkili` = web
 * portalı (personel + firma evrakı). Config rolünden türetilir (`Yetkili` =
 * `Settings:Guest:PortalAccessRoleUuid`, aksi `Personeli`) — istemci burada yalnız gösterir.
 */
export type ContractorAccessLevel = 'Personeli' | 'Yetkili';

/**
 * Taşeron personeli / bekleyen davet — api-contract §13.3/§13.6 (`ContractorPersonnelDto`).
 * Liste **aktive personel + bekleyen düz davetleri** birlikte taşır (`kind` ayırır; kişiye
 * hedefli bağlantılar artık Invitation satırı olarak listelenmez — kişinin `pendingLink`'idir). `uuid` =
 * aktive personelde `SupplierUser`, davette `GuestInvitation` public kimliği (düzenle/sil
 * `Personnel` uuid'iyle, iptal `Invitation` uuid'iyle yapılır). `emailOrPhone` giriş/davet
 * hedefi (e-posta öncelikli). `invitedAt` davetlerde, `activatedAt` aktive personelde dolu
 * (`NoLogin` kaydında **oluşturma anı**). Sıralı iç `Id` **sızmaz**.
 */
export interface ContractorPersonnelDto {
  uuid: string;
  kind: ContractorPersonnelKind;
  firstName: string;
  lastName: string;
  emailOrPhone: string | null;
  /** (#29) Personel satırlarında dolu; davet satırlarında daima `null`. `personalDataMasked` ise maskeli. */
  email: string | null;
  /** (#29) `email` ile aynı kurallar. Maskesizken kanonik "+CC-NNNN…". */
  phone: string | null;
  status: ContractorPersonnelStatus;
  accessLevel: ContractorAccessLevel;
  /** Davet oluşturulma anı — UTC ISO (§4); davetlerde dolu. */
  invitedAt: string | null;
  /** Aktivasyon anı — UTC ISO (§4); aktive personelde dolu. */
  activatedAt: string | null;
  /**
   * T.C. kimlik numarası (api-contract §13, #29). Supplier yüzeyi: tam. Ana firma (EP):
   * `contractors:personal-data` yetkisi varsa tam, yoksa maskeli (`•••••••••NN`). `null` =
   * kayıtta T.C. yok (maskeleme `null`'ı `null` bırakır); davet satırlarında daima `null`.
   * Maskeli olduğu `personalDataMasked` ile söylenir — değerden çıkarılmaz. Maskeli değer
   * yalnız gösterim içindir: geri gönderilmez, arama anahtarı olmaz.
   */
  tcKimlikNo: string | null;
  /**
   * Ham `SupplierUser.IsActive` — **YENİ**, S26 düzeltmesi, opsiyonel (api-contract §13.3/13.6
   * DTO notu, ikinci code-review turunda genişletildi). **Doldurulur:** supplier tekil profil
   * (`GET`/`PUT /supplier/contractor/personnel/{userUuid}`, §13.6) **ve her iki liste ucu**
   * (`GET .../personnel` çoğul — §13.3 ana firma **ve** §13.6 supplier) **ve** ana firma
   * `PUT /contractors/{uuid}/personnel/{userUuid}` (§13.3) yanıtı. Ana firmada tekil bir `GET`
   * YOKTUR; supplier'ın §13.6 tekil `GET`ini web çağırmaz — iki portalda da çekmecenin düzenleme
   * formu (`usePersonnelEditor`) tıklama anındaki LİSTE satırından hidratlanır, dolayısıyla liste
   * de doldurulmak ZORUNDADIR. **Doldurulmaz:** `Invitation`
   * satırları (kavram yok) ve create/remove yanıtları (ilgisiz). `status` TÜRETİLMİŞ ve
   * KAYIPLIDIR: `NoLogin`, `PasswordHash == null` iken `IsActive`'in gerçek değeri `true` da
   * `false` da olsa görünür (`PasswordHash == null` her zaman `IsActive`'den ÖNCE gelir) —
   * istemci `isActive`'i `status`'ten (`status !== 'Inactive'`) **ASLA türetmemelidir**. İki
   * `PUT` de `isActive`'i her istekte yazar; bu alanı round-trip etmek ZORUNDADIR: yanlış
   * türetilmiş bir `isActive`, yalnızca adı (veya ana firma tarafında `accessLevel`'i)
   * düzenlenen bir `NoLogin` + pasif kaydı **sessizce yeniden aktifleştirir**. Beklenmedik bir `null` görülürse
   * (kind === 'Personnel' iken sözleşme bunu garanti eder, ama tip yine de izin verir) form
   * **tahminle açılmaz** — ne `!` ne `?? true`/`?? false` ile.
   */
  isActive: boolean | null;
  /** Yalnız kind === 'Invitation' satırında dolu; [] = geçerli iletişimi olmayan eski satır; Personnel'da null. */
  availableChannels: InviteContactChannel[] | null;
  /** GL6 §4.6 — yalnız status === 'NoLogin' Personnel satırlarında dolu; Invitation dahil diğer her satırda null. */
  pendingLink: PersonnelActivationLinkDto | null;
  /** (#29) Sunucu bu yanıta maskeleme politikasını uyguladıysa `true`. Supplier yüzeyinde daima `false`. */
  personalDataMasked: boolean;
}

/**
 * Personel düzenleme isteği — api-contract §13.3 (`PUT /contractors/{uuid}/personnel/{userUuid}`).
 * Yalnız **aktive personel** için. GL29: `firstName`/`lastName` `null` (ya da anahtar yok) =
 * değişmez; dize = yaz (kırpılır, ≤ 100); boş/whitespace dize → `400 VALIDATION_FAILED`
 * (`FIELD_EMPTY`). İstemci dolu bir adı boşaltamaz (form engeller) ve `null`'ı yalnız kayıtlı ad
 * boşken alan boş bırakıldığında gönderir — adsız kişi ad uydurmadan pasife alınır/erişimi
 * değişir. `accessLevel` yalnız
 * `Personeli`|`Yetkili` (ilgili config rolü atanır, zıt config rolü kaldırılır; keyfi rol değil;
 * grant yazılmaz). `isActive` liste satırının HAM değeridir, `status`'ten türetilmez; `false` →
 * personel pasif (login/scope reddi). Personel yok/kapsam dışı → `404 PERSONNEL_NOT_FOUND`; hedef
 * config rolü tanımsız → `409 ACCESS_LEVEL_ROLE_NOT_CONFIGURED` (mutasyon yapılmaz).
 */
export interface ContractorPersonnelUpdateRequest {
  /** `null` = değişmez; asla `""` gönderilmez. */
  firstName: string | null;
  lastName: string | null;
  accessLevel: ContractorAccessLevel;
  isActive: boolean;
  /**
   * Üç durumlu (GL29): anahtar yok = değişmez · `null` = temizle · dize = yaz (§13.6 T.C. kuralları).
   * Anahtar YALNIZ çağıran `contractors:personal-data` yetkisine sahipken (`personalDataMasked ===
   * false`) VE kullanıcı boş bir T.C.'ye değer yazdığında gönderilir; aksi hâlde hiç konmaz.
   * Yetkisiz anahtar (`null` dahil) → `403 PERSONNEL_TC_WRITE_FORBIDDEN`, hiçbir alan yazılmaz.
   * Maskeli değer ASLA geri gönderilmez.
   */
  tcKimlikNo?: string | null;
}

/**
 * Taşeron saha erişimi — api-contract §13.4 (`ContractorSiteDto`). `uuid` erişim kaydının
 * (`ContractorSiteAccess`), `siteUuid` sahanın public kimliği (revoke bununla).
 * `siteName`/`siteCode` tenant çekirdeğinden çözülür.
 */
export interface ContractorSiteDto {
  uuid: string;
  siteUuid: string;
  siteName: string;
  siteCode: string | null;
  createdAt: string | null;
}

/** §5.10.1 — wire'da PascalCase string. */
export type InviteChannel = 'Email' | 'Sms' | 'Both';
/** Bir davetin saklı iletişiminden türeyen tek kanal (§13 ContractorPersonnelDto.availableChannels). */
export type InviteContactChannel = 'Email' | 'Sms';

/** §5.10.1 / GL6 §4.12 ActivationLinkResult.dispatch */
export interface InviteDispatch {
  /** Sunucunun çözdüğü kanal (istenen ya da otomatik). 200'de daima dolu. */
  channel: InviteChannel;
  /** `channel` e-postayı içeriyorsa: gönderim denendi ve sağlayıcı hata vermedi mi. Aksi hâlde daima false. */
  emailSent: boolean;
  /** `channel` SMS'i içeriyorsa: gönderim denendi ve sağlayıcı hata vermedi mi. Aksi hâlde daima false. */
  smsSent: boolean;
}

/**
 * Bir aktivasyon bağlantısı yanıtı — GL6 §4.12 (`ActivationLinkResult`). Create / send-link / resend
 * hepsi bunu döner. `activationLink` **loglanmaz**
 * (yalnız ekranda gösterilir/kopyalanır). Gönderimde yenilenen `invitationUuid` bir öncekini
 * geçersiz kılar: istemci eldeki uuid'i bununla DEĞİŞTİRİR ve listeyi tazeler (§5 madde 4).
 */
export interface ActivationLinkResult {
  invitationUuid: string;
  activationLink: string;
  /** UTC ISO (§4). */
  expiresAt: string;
  dispatch: InviteDispatch;
}

/** GL6 §4.12 — bir personelin bekleyen aktivasyon bağlantısı (yalnız `status === 'NoLogin'` Personnel satırında dolu). */
export interface PersonnelActivationLinkDto {
  invitationUuid: string;
  status: 'Pending' | 'Expired';
  /** Son gönderim anı (her gönderim yeni bir satırdır). */
  sentAt: string;
  expiresAt: string;
  /** Bağlantının iletişim etiketi; `contractors:personal-data` yetkisiz EP çağıranı için maskeli. */
  emailOrPhone: string | null;
  availableChannels: InviteContactChannel[];
  /** sentAt + bekleme süresi. Yalnız ipucu: sunucunun 429'u otoritedir. */
  resendAvailableAt: string;
}

/** `POST /contractors/{uuid}/personnel` (EP) ve `POST /supplier/contractor/personnel` (SP) — GL6 §4.2. */
export interface CreatePersonnelRequest {
  firstName: string;
  lastName: string;
  /** EP: zorunlu. SP: e-posta veya telefon verilince zorunlu (belge analizi yakalama çağrısı T.C.'siz kalır). */
  tcKimlikNo?: string | null;
  /** Varsayılan 'Personeli'. */
  accessLevel?: ContractorAccessLevel;
  email?: string | null;
  phone?: string | null;
  /** Yalnız e-posta ve telefon birlikte verilince gönderilir (GL3 istemci kuralı). */
  channel?: InviteChannel;
}

export interface CreatePersonnelResult {
  /**
   * Gerçek sonuç (D1): `kind: 'Personnel'` (`status: 'NoLogin'`) ya da `kind: 'Invitation'`
   * (`status: 'Pending'`). Başarı METNİNİ asla `kind`'dan seçme — yalnız `link !== null`'dan.
   */
  personnel: ContractorPersonnelDto;
  /** İletişim verilmediyse `null`. */
  link: ActivationLinkResult | null;
}

/** `POST …/personnel/{userUuid}/activation-link` (EP ve SP) — GL6 §4.3. En az bir iletişim. */
export interface SendActivationLinkRequest {
  email?: string | null;
  phone?: string | null;
  channel?: InviteChannel;
}

/** EP `POST /contractors/{uuid}/invitations/{invitationUuid}/resend`. SP `{ invitationUuid, channel? }` kalır. */
export interface ResendActivationLinkRequest {
  channel?: InviteChannel;
}

/** `429 ACTIVATION_LINK_COOLDOWN` `messageData` (UTC ISO). */
export interface ActivationLinkCooldownData {
  availableAt: string;
}

/**
 * Taşeron oluşturma isteği — api-contract §13.1 (`POST /contractors`). `name` zorunlu;
 * `hazardClass` 1-3. Yeni kayıt varsayılan `status = Active` (istekte yok). Boş/whitespace
 * opsiyonel alanlar `null` gönderilir.
 */
export interface ContractorWriteRequest {
  name: string;
  legalName?: string | null;
  taxNumber?: string | null;
  taxOffice?: string | null;
  isgKatipNo?: string | null;
  hazardClass: HazardClass;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  contractStart?: string | null;
  contractEnd?: string | null;
}

/**
 * Taşeron güncelleme isteği — api-contract §13.1 (`PUT /contractors/{uuid}`). Create
 * alanları + opsiyonel `status` (verilirse `Active`|`Suspended`|`Inactive`; null/atlanırsa
 * mevcut korunur).
 */
export interface ContractorUpdateRequest extends ContractorWriteRequest {
  status?: ContractorStatus | null;
}

/**
 * Uygunluk süzgeci — api-contract §13.1 (`compliance`, YENİ S34; küçük harf, case-insensitive).
 * `expired` = taşeronun en az bir GÜNCEL belgesi `Expired`; `expiring` = en az bir GÜNCEL
 * belgesi `Expiring` (§13 güncel belge kuralı — tür başına tek güncel belge). İki değer
 * KESİŞEBİLİR (`overall`'a göre süzmek DEĞİLDİR). `status` ile AND'lenir. Tanınmayan değer →
 * süzgeç uygulanmaz (`400` yok). §34.1 `contractorsWithExpiredDocs`/`contractorsWithExpiringDocs`
 * KPI'ının drill-through hedefi: `GET /contractors?status=Active&compliance=expired|expiring` →
 * `filteredTotal` KPI sayısına EŞİTTİR.
 */
export type ContractorComplianceFilter = 'expired' | 'expiring';

/**
 * Taşeron liste/filtre parametreleri — api-contract §13.1 (`GET /contractors`) + §3 paging.
 * `search` ad/vergi no üzerinde case-insensitive substring; `status` case-insensitive
 * (tanınmazsa uygulanmaz); `compliance` (YENİ S34) yalnız `filteredTotal`'ı etkiler. Sıra
 * backend'de ad artan (istemci sort göndermez).
 */
export interface ContractorListParams {
  status?: ContractorStatus;
  /** Güncel belge uygunluğuna göre süzer (YENİ S34) — `status` ile AND'lenir. */
  compliance?: ContractorComplianceFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Uygunluk belgesi linkleme isteği — api-contract §13.2 (`POST /contractors/{uuid}/documents`).
 * `documentUuid` §8'de **confirmed + aynı firma** olmalı (aksi hâlde `404 DOCUMENT_NOT_FOUND`).
 * `type` tanınmaz / `documentUuid` boş → `400 VALIDATION_FAILED`. Tarihler DateTime (UTC ISO
 * 8601, §4) — bir gün değeri (`2026-08-08`) günün başına bağlanır ve öyle geri okunur.
 */
export interface ContractorDocumentLinkRequest {
  /** `company` entity'sinin AKTİF §27 kayıt kodu (pasif → `INACTIVE`, bilinmeyen → `INVALID`). */
  type: string;
  documentUuid: string;
  validFrom?: string | null;
  validUntil?: string | null;
  note?: string | null;
}

/**
 * Saha erişimi ekleme isteği — api-contract §13.4 (`POST /contractors/{uuid}/sites`).
 * `siteUuid` çağıranın firmasında bir saha olmalı (yok/başka firma → `404 SITE_NOT_FOUND`).
 * Idempotent: saha zaten aktifse var-olan erişim döner (200).
 */
export interface ContractorSiteAddRequest {
  siteUuid: string;
}

/** Derleme-zamanı güvencesi isteyen form katmanı için (opsiyonel yardımcı). */
export type InviteContactInput =
  | { email: string; phone?: null; channel?: null }
  | { email?: null; phone: string; channel?: null }
  | { email: string; phone: string; channel: InviteChannel };

// ---- GL3 davet iletişimi — EP + SP'nin ORTAK kuralları (api-contract §5.10.1) ----
// Formlar ayrı bileşen kalır (CTO kararı); istemci doğrulama aynası, istek kuralı, alan-hata ve
// sonuç eşlemesi burada TEK yerde yaşar ki iki portal ayrışmasın. Metin YOK — her app kendi
// i18n anahtarına eşler.

/** Davet formunun iletişim alanı = sunucunun `errors[].field` / `messageData.fields` değeri. */
export type InviteContactField = 'email' | 'phone';

/** İletişim alanı hata türü: sunucunun `REQUIRED` / `INVALID`'i + `409` çakışması (`TAKEN`). */
export type InviteFieldErrorCode = 'REQUIRED' | 'INVALID' | 'TAKEN';

export interface InviteFieldError {
  field: InviteContactField;
  code: InviteFieldErrorCode;
}

/** §5.10.1 e-posta kuralının istemci aynası (danışma amaçlı; otorite sunucudur). */
const INVITE_EMAIL_RE = /^[^\s@]+@[^\s@.][^\s@]*\.[^\s@]*[^\s@.]$/;
const INVITE_EMAIL_MAX_LENGTH = 256;
/** §5.10.1 telefon kuralının aynası: TR cep (`+90`/`0` önekli ya da öneksiz) veya `+` ile E.164. */
const INVITE_PHONE_RE = /^(?:\+905\d{9}|05\d{9}|5\d{9}|\+(?!90)[1-9]\d{7,14})$/;
/**
 * D3 ön-denetimi (spec §5.2 `PHONE_CHARS_RE`): kırpılmış HAM değerde yalnız `[0-9 boşluk - ( ) . +]`,
 * `+` yalnız ilk karakter. Temizlemeden ÖNCE uygulanır — aksi hâlde `(+90) 532 111 22 33` istemciden
 * geçip sunucuda `phone`/`INVALID` olurdu.
 */
const INVITE_PHONE_CHARS_RE = /^\+?[\d\s\-().]*$/;

/** Sunucunun ön-ayıklamasıyla aynı: boşluk, `-`, `(`, `)` ve `.` atılır. */
function invitePhoneKey(raw: string): string {
  return raw.trim().replace(/[\s\-().]/g, '');
}

/** Spec §5.2 `phoneOk`: önce ham değerin karakter kümesi, sonra temizlenmiş anahtar. */
function invitePhoneOk(trimmed: string): boolean {
  return INVITE_PHONE_CHARS_RE.test(trimmed) && INVITE_PHONE_RE.test(invitePhoneKey(trimmed));
}

/**
 * §5.10.1 iletişim kurallarının istemci aynası — sunucuyla AYNI kodlar ve AYNI "ikisi de boş →
 * her alana bir `REQUIRED`" kuralı; dolu alan biçim dışıysa o alana `INVALID`. Boş dizi = geçerli.
 */
export function inviteContactIssues(email: string, phone: string): InviteFieldError[] {
  const e = email.trim();
  const p = phone.trim();
  if (e === '' && p === '') {
    return [
      { field: 'email', code: 'REQUIRED' },
      { field: 'phone', code: 'REQUIRED' },
    ];
  }
  const issues: InviteFieldError[] = [];
  if (e !== '' && (e.length > INVITE_EMAIL_MAX_LENGTH || !INVITE_EMAIL_RE.test(e))) {
    issues.push({ field: 'email', code: 'INVALID' });
  }
  if (p !== '' && !invitePhoneOk(p)) {
    issues.push({ field: 'phone', code: 'INVALID' });
  }
  return issues;
}

/**
 * §5.2 istek kuralı: boş/whitespace iletişim `null`; `channel` YALNIZ iki iletişim birlikte
 * doluyken (tek iletişimde kanal iletişimden türer ve gönderilmez). İkisi de boşsa `null` —
 * form doğrulaması bunu gönderimden önce keser.
 */
export function toInviteContact(
  email: string,
  phone: string,
  channel: InviteChannel,
): InviteContactInput | null {
  const e = email.trim();
  const p = phone.trim();
  if (e !== '' && p !== '') {
    return { email: e, phone: p, channel };
  }
  if (e !== '') {
    return { email: e, phone: null };
  }
  if (p !== '') {
    return { email: null, phone: p };
  }
  return null;
}

/**
 * §5.3 — sunucu hatasının davet iletişim alanlarına bağlanan kısmı: `400 VALIDATION_FAILED`
 * `errors[]`'tan `email`/`phone` + `REQUIRED`/`INVALID`; `409 GUEST_INVITE_CONTACT_ALREADY_EXISTS`
 * `messageData.fields`'tan `TAKEN`. Boş dizi = alana bağlanamadı → çağıran banner gösterir.
 * Başka her giriş eşlenMEZ — özellikle model binding'in `$.channel`/`FIELD_INVALID`'i (reddedilen kanal
 * değeri, spec §2.3): kanal alan hatası diye bir şey yok, genel `VALIDATION_FAILED` banner'ına düşer.
 */
export function inviteFieldErrors(error: unknown): InviteFieldError[] {
  if (!isApiError(error)) {
    return [];
  }
  const out: InviteFieldError[] = [];
  if (error.errorCode === 'VALIDATION_FAILED') {
    for (const item of error.errors) {
      if (
        (item.field === 'email' || item.field === 'phone') &&
        (item.code === 'REQUIRED' || item.code === 'INVALID')
      ) {
        out.push({ field: item.field, code: item.code });
      }
    }
  } else if (error.errorCode === 'GUEST_INVITE_CONTACT_ALREADY_EXISTS') {
    const fields = error.messageData?.fields;
    if (Array.isArray(fields)) {
      for (const field of fields as unknown[]) {
        if (field === 'email' || field === 'phone') {
          out.push({ field, code: 'TAKEN' });
        }
      }
    }
  }
  return out;
}

/** §5.4 — sonuç adımında planlanan bir kanal ve gönderilip gönderilemediği. */
export interface InviteDispatchLine {
  channel: InviteContactChannel;
  sent: boolean;
}

/**
 * §5.4 — `dispatch.channel` planındaki her kanal için bir satır (sıra sabit: önce e-posta).
 * Planda olup `sent: false` olan kanal = denendi, gönderilemedi (bağlantı elle iletilir). Hiçbir
 * kanal gönderilemediyse çağıran satırlar yerine tek "otomatik gönderilemedi" notunu gösterir.
 */
export function inviteDispatchLines(dispatch: InviteDispatch): InviteDispatchLine[] {
  const lines: InviteDispatchLine[] = [];
  if (dispatch.channel !== 'Sms') {
    lines.push({ channel: 'Email', sent: dispatch.emailSent });
  }
  if (dispatch.channel !== 'Email') {
    lines.push({ channel: 'Sms', sent: dispatch.smsSent });
  }
  return lines;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

// ============================================================================
// 13.1 Taşeronlar (CRUD)
// ============================================================================

/**
 * `GET /contractors` (`list`) — firmanın taşeronları (sayfalı + filtreli). Her kayıt
 * uygunluk özetiyle (`complianceStatus`) döner. Sıra ad artan (istemci sort göndermez).
 */
export function listContractors(params: ContractorListParams = {}) {
  return api.list<ContractorDto>('contractors', {
    searchParams: {
      status: params.status,
      compliance: params.compliance,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/** `GET /contractors/{uuid}` (`get`) — tek taşeron (yok/kapsam dışı → `404 CONTRACTOR_NOT_FOUND`). */
export function getContractor(uuid: string) {
  return api.get<ContractorDto>(`contractors/${uuid}`);
}

/**
 * `POST /contractors` (`create`) — taşeron oluşturur; yeni kimlik `value.uuid` (201).
 * `name` boş → `400 VALIDATION_FAILED`; `hazardClass` 1-3 dışı → `400`.
 */
export function createContractor(body: ContractorWriteRequest) {
  return api.post<ContractorDto>('contractors', { json: body });
}

/** `PUT /contractors/{uuid}` (`update`) — taşeron günceller (yok → `404 CONTRACTOR_NOT_FOUND`). */
export function updateContractor(uuid: string, body: ContractorUpdateRequest) {
  return api.put<ContractorDto>(`contractors/${uuid}`, { json: body });
}

/** `DELETE /contractors/{uuid}` (`delete`) — **soft-delete** (yok → `404 CONTRACTOR_NOT_FOUND`). */
export function deleteContractor(uuid: string) {
  return api.delete<Empty>(`contractors/${uuid}`);
}

// ============================================================================
// 13.2 Uygunluk Belgeleri (§8 link)
// ============================================================================

/**
 * `GET /contractors/{uuid}/documents` (`get`) — taşeronun uygunluk belgeleri (tür +
 * `documentUuid` + geçerlilik + §8 `fileName`). Sıra oluşturulma azalan. Taşeron
 * yok/kapsam dışı → `404 CONTRACTOR_NOT_FOUND`. Sayfasız dizi (zarf `value`).
 */
export function listContractorDocuments(uuid: string) {
  return api.get<ContractorDocumentDto[]>(`contractors/${uuid}/documents`);
}

/**
 * `POST /contractors/{uuid}/documents` (`update`) — §8'de confirm edilmiş dosyayı taşerona
 * link'ler; yeni kimlik `value.uuid` (201). `documentUuid` confirmed + aynı firma olmalı
 * (yoksa `404 DOCUMENT_NOT_FOUND`); `type` tanınmaz/`documentUuid` boş → `400 VALIDATION_FAILED`.
 */
export function linkContractorDocument(uuid: string, body: ContractorDocumentLinkRequest) {
  return api.post<ContractorDocumentDto>(`contractors/${uuid}/documents`, { json: body });
}

/**
 * `DELETE /contractors/{uuid}/documents/{linkUuid}` (`update`) — belge linkini
 * **soft-delete** eder. Belge yok/taşerona ait değil → `404 CONTRACTOR_DOCUMENT_NOT_FOUND`.
 *
 * ⚠️ Silme hedefi **link kaydının kendi `uuid`'idir** (`ContractorDocumentDto.uuid` ← entity.Uuid),
 * §8 storage `documentUuid`'i **DEĞİL** (backend `d => d.Uuid == …` ile çözer; ikisi ayrı GUID).
 * §8 `documentUuid` gönderilirse her silme `404 CONTRACTOR_DOCUMENT_NOT_FOUND` döner. İndirme ise
 * §8 presigned GET ile `documentUuid` üzerinden yapılır.
 */
export function deleteContractorDocument(uuid: string, linkUuid: string) {
  return api.delete<Empty>(`contractors/${uuid}/documents/${linkUuid}`);
}

// ============================================================================
// 13.3 Personel
// ============================================================================

/**
 * `GET /contractors/{uuid}/personnel` (`get`) — taşerona bağlı **aktive personel**
 * (`SupplierUser`) + **bekleyen davetler** (`GuestInvitation`) birlikte; her satır `kind`
 * (Personnel/Invitation), `status` (Active/Inactive | Pending/Expired/Revoked) ve
 * `accessLevel` (Personeli/Yetkili) taşır. Sıra: önce aktive personel (soyad, ad), sonra
 * davetler (en yeni önce). Taşeron yok/kapsam dışı → `404 CONTRACTOR_NOT_FOUND`. Sayfasız dizi.
 */
export function listContractorPersonnel(uuid: string) {
  return api.get<ContractorPersonnelDto[]>(`contractors/${uuid}/personnel`);
}

/**
 * `PUT /contractors/{uuid}/personnel/{userUuid}` (`update`) — bir **aktive personelin** adını,
 * erişim seviyesini (`accessLevel`: Personeli/Yetkili → config rolü), aktif/pasif durumunu
 * (`isActive`) ve — yalnız `contractors:personal-data` ile, boşken — T.C.'sini günceller (GL29).
 * Yalnız aktive personel; bekleyen davet düzenlenmez (iptal + yeniden davet). Hata sırası
 * §13.3'te: `400 VALIDATION_FAILED` → `403 PERSONNEL_TC_WRITE_FORBIDDEN` → `400 TC_KIMLIK_INVALID`
 * → `404 CONTRACTOR_NOT_FOUND`/`PERSONNEL_NOT_FOUND` → `409 PERSONNEL_TC_ALREADY_SET` →
 * `409 PERSONNEL_TC_ALREADY_EXISTS` → `409 ACCESS_LEVEL_ROLE_NOT_CONFIGURED`. → güncel satır
 * (`kind = Personnel`), kitle kuralıyla maskeli. Gövdeyi yalnız `apps/web-expert`
 * `usePersonnelClient` kurar.
 */
export function updatePersonnel(
  contractorUuid: string,
  userUuid: string,
  body: ContractorPersonnelUpdateRequest,
) {
  return api.put<ContractorPersonnelDto>(`contractors/${contractorUuid}/personnel/${userUuid}`, {
    json: body,
  });
}

/**
 * `DELETE /contractors/{uuid}/personnel/{userUuid}` (`update`) — aktive personeli **bu
 * taşerondan çıkarır** (bu firma + taşerona ait `SupplierUserCompany` atamasını kaldırır;
 * personelin başka firmalardaki atamaları + `SupplierUser` kaydı korunur). Personel yok/bu
 * taşerona bağlı değil → `404 PERSONNEL_NOT_FOUND`; taşeron yok → `404 CONTRACTOR_NOT_FOUND`.
 */
export function deletePersonnel(contractorUuid: string, userUuid: string) {
  return api.delete<Empty>(`contractors/${contractorUuid}/personnel/${userUuid}`);
}

/**
 * `DELETE /contractors/{uuid}/invitations/{invitationUuid}` (`update`) — bekleyen bir bağlantıyı
 * iptal eder: kişiye hedefli aktivasyon bağlantısı (anahtar = `pendingLink.invitationUuid`) ya da
 * düz davet (Invitation satırının `uuid`'i). İptal edilen düz davet listeden KAYBOLUR ("İptal
 * edildi" satırı yok). Davet yok/aktive/kapsam dışı → `404 INVITATION_NOT_FOUND`; taşeron yok →
 * `404 CONTRACTOR_NOT_FOUND`.
 */
export function revokeInvitation(contractorUuid: string, invitationUuid: string) {
  return api.delete<Empty>(`contractors/${contractorUuid}/invitations/${invitationUuid}`);
}

/**
 * `POST /contractors/{uuid}/personnel` (`update`) — GL6 §4.2. T.C. her zaman zorunlu. İletişim
 * verilirse aynı çağrıda aktivasyon bağlantısı da gönderilir (`link` dolu); başka bir hesaba ait
 * iletişimde sunucu kişi YAZMAZ, düz davet gönderir ve yine `201` döner (D1). `201`.
 */
export function createPersonnel(contractorUuid: string, body: CreatePersonnelRequest) {
  return api.post<CreatePersonnelResult>(`contractors/${contractorUuid}/personnel`, { json: body });
}

/** `POST /contractors/{uuid}/personnel/{userUuid}/activation-link` (`update`) — GL6 §4.3. */
export function sendActivationLink(
  contractorUuid: string,
  userUuid: string,
  body: SendActivationLinkRequest,
) {
  return api.post<ActivationLinkResult>(
    `contractors/${contractorUuid}/personnel/${userUuid}/activation-link`,
    { json: body },
  );
}

/** `POST /contractors/{uuid}/invitations/{invitationUuid}/resend` (`update`) — GL6 §4.4. Yeni `invitationUuid` döner. */
export function resendActivationLink(
  contractorUuid: string,
  invitationUuid: string,
  body: ResendActivationLinkRequest,
) {
  return api.post<ActivationLinkResult>(
    `contractors/${contractorUuid}/invitations/${invitationUuid}/resend`,
    { json: body },
  );
}

// ============================================================================
// 13.4 Saha Erişimi
// ============================================================================

/**
 * `GET /contractors/{uuid}/sites` (`get`) — taşeronun erişebildiği sahalar (ad/kod tenant
 * çekirdeğinden). Sıra saha adı. Taşeron yok → `404 CONTRACTOR_NOT_FOUND`. Sayfasız dizi.
 */
export function listContractorSites(uuid: string) {
  return api.get<ContractorSiteDto[]>(`contractors/${uuid}/sites`);
}

/**
 * `POST /contractors/{uuid}/sites` (`update`) — saha erişimi ekler (idempotent: zaten
 * aktifse var-olan erişim 200; yeni 201 + `value.uuid`). Saha yok/başka firma →
 * `404 SITE_NOT_FOUND`; taşeron yok → `404 CONTRACTOR_NOT_FOUND`.
 */
export function addContractorSite(uuid: string, body: ContractorSiteAddRequest) {
  return api.post<ContractorSiteDto>(`contractors/${uuid}/sites`, { json: body });
}

/**
 * `DELETE /contractors/{uuid}/sites/{siteUuid}` (`update`) — saha erişimini **soft-delete**
 * eder (`siteUuid` = sahanın public kimliği). Taşeron yok → `404 CONTRACTOR_NOT_FOUND`;
 * saha yok/başka firma → `404 SITE_NOT_FOUND`; aktif erişim yok → `404 CONTRACTOR_SITE_NOT_FOUND`.
 */
export function removeContractorSite(uuid: string, siteUuid: string) {
  return api.delete<Empty>(`contractors/${uuid}/sites/${siteUuid}`);
}

// ============================================================================
// 15.1 Personel Belgeleri — yol A (ana firma / web-expert), tenant-scoped
// ============================================================================
//
// KAYNAK: api-contract.md §15 (Personel Belgeleri — yol A). Bir taşeron **personelinin**
// kişisel/hassas evrakı (kimlik/sağlık/adli sicil…). Firma genel uygunluk belgesi
// (§13 `ContractorDocument`) firma düzeyindeyken bu **personel bazlıdır** (tek `SupplierUser`).
// Fiziksel dosya §8 Storage'da yaşar; bu modül yalnız **link + geçerlilik** taşır (yükleme
// §8 `uploadDocument` → confirm → `documentUuid`; indirme §8 `getDocument(uuid)` presigned GET).
// Yetki modülü **`contractors`** (§15.1; belge listele → `get`, ekle/sil → `update`).
//
// **KVKK/PII.** Kimlik/sağlık/adli sicil hassastır: içerik §8 presigned ile servis edilir;
// istemci PII/dosya URL'i/`documentUuid`'i **log'lamaz**. Belge türü yalnız etikettir.

/**
 * Personel belgesi onay durumu — api-contract §15.4 (`PersonnelDocumentDto.approvalStatus`,
 * PascalCase wire; DB'de string). Her belge `Pending` başlar (ana firma / supplier yetkili /
 * personel-self ve §20.2 doküman-analizi kabul akışı dahil); İSG uzmanı (yalnız yol A) belgeyi
 * `Approved`/`Rejected` yapar. Bu durum giriş izni kapısının (§17.2/§22 tip-bazlı
 * `requiredDocumentsSatisfied`) belge boyutu girdisidir: bir personel iznin tipinde **istenen tüm
 * belge türlerine** `Approved` belgeyle sahipse belge önkoşulu karşılandı (aksi `missingDocumentTypes`
 * dolu). İstemci bu değeri yalnız gösterir (rozet), yeniden hesaplamaz.
 */
export type PersonnelDocumentApprovalStatus = 'Pending' | 'Approved' | 'Rejected';

/**
 * Personel belgesi linki — api-contract §15 (`PersonnelDocumentDto`). Her kayıt §8 Storage'daki
 * confirmed bir dosyaya link'tir; `fileName` §8'den çözülür (indirme §8 `getDocument`).
 * `validityStatus` (`ComplianceStatus`) tür başına türetilir (§15, GL #13);
 * `Approved && (Valid || Expiring)` ⇔ §17.2 kapısına sayar; §13 şirket belgesi türetimi DEĞİL. Belge sahibi personel
 * çağrı bağlamından bellidir (path `userUuid`) — DTO'da tekrarlanmaz. Sıralı iç `Id` **sızmaz**.
 */
export interface PersonnelDocumentDto {
  uuid: string;
  /** `personnel` entity'sinin §27 kayıt kodu (PascalCase; pasif olabilir; etiket §27 kaydından). */
  type: string;
  documentUuid: string;
  /** §8 dokümanından çözülen özgün dosya adı (indirme için). */
  fileName: string | null;
  /** Geçerlilik başlangıcı — DateTime (UTC ISO 8601, §4). Gün göstermek için kırpılmalıdır. */
  validFrom: string | null;
  /**
   * Geçerlilik bitişi — DateTime (UTC ISO 8601, §4); DateOnly **değildir**. Bir gün değeri
   * (`2026-08-08`) gönderilse bile `2026-08-08T00:00:00.000Z` olarak geri okunur, yani gün
   * göstermek isteyen ekran değeri kendisi kırpar. Türetilmiş durumun kaynağı.
   */
  validUntil: string | null;
  validityStatus: ComplianceStatus;
  note: string | null;
  /**
   * Onay durumu (İSG uzmanı; §15.4) — türetilmez, saklanır. `Pending` başlar; onay/red
   * yalnız yol A (web-expert). Giriş izni kapısının (§17.2) belge boyutu girdisi.
   */
  approvalStatus: PersonnelDocumentApprovalStatus;
  /** Onaylayan/reddeden İSG uzmanının adı (identity'den çözülür; `Pending` iken `null`). */
  approvedByName: string | null;
  /** Onay/red anı — UTC ISO (§4; DateTime). `Pending` iken `null`. */
  approvedAt: string | null;
  /** Red gerekçesi — yalnız `Rejected`'ta dolu (onay önceki gerekçeyi temizler). */
  rejectionReason: string | null;
  createdAt: string | null;
  /** GL #7 §4.2: bu dosyayı bu (entity, type) olarak doğrulayan doc-check sürümü; `null` = AI doğrulamadı (elle yükleme vb.). Yalnız sunucu yazar. */
  recognitionRelease: number | null;
}

/**
 * Personel belgesi linkleme isteği — api-contract §15.1 (`POST …/personnel/{userUuid}/documents`).
 * `documentUuid` §8'de **confirmed + aynı firma** olmalı (aksi `404 DOCUMENT_NOT_FOUND`); `type`
 * tanınmalı, `documentUuid` boş olmamalı (aksi `400 VALIDATION_FAILED`). Tarihler DateTime (UTC
 * ISO 8601, §4) — bir gün değeri (`2026-08-08`) günün başına bağlanır ve öyle geri okunur.
 */
export interface PersonnelDocumentLinkRequest {
  /** `personnel` entity'sinin AKTİF §27 kayıt kodu (pasif → `INACTIVE`, bilinmeyen → `INVALID`). */
  type: string;
  documentUuid: string;
  validFrom?: string | null;
  validUntil?: string | null;
  note?: string | null;
}

/**
 * Belge reddetme isteği — api-contract §15.1/§15.4 (`POST …/documents/{linkUuid}/reject`).
 * `reason` **zorunlu** (boş/whitespace → `400 VALIDATION_FAILED`, alan `reason`).
 */
export interface PersonnelDocumentRejectRequest {
  reason: string;
}

/**
 * `GET /contractors/{uuid}/personnel/{userUuid}/documents` (`get`) — personelin belgeleri
 * (tür + geçerlilik + §8 dosya adı). Sıra oluşturulma azalan. Taşeron yok → `404
 * CONTRACTOR_NOT_FOUND`; personel yok/kapsam dışı → `404 PERSONNEL_NOT_FOUND`. Sayfasız dizi.
 */
export function listPersonnelDocuments(contractorUuid: string, userUuid: string) {
  return api.get<PersonnelDocumentDto[]>(
    `contractors/${contractorUuid}/personnel/${userUuid}/documents`,
  );
}

/**
 * `POST /contractors/{uuid}/personnel/{userUuid}/documents` (`update`) — §8'de confirm edilmiş
 * dosyayı personele link'ler; yeni kimlik `value.uuid` (201). `documentUuid` confirmed + aynı
 * firma (yoksa `404 DOCUMENT_NOT_FOUND`); `type` tanınmaz/`documentUuid` boş → `400 VALIDATION_FAILED`.
 */
export function linkPersonnelDocument(
  contractorUuid: string,
  userUuid: string,
  body: PersonnelDocumentLinkRequest,
) {
  return api.post<PersonnelDocumentDto>(
    `contractors/${contractorUuid}/personnel/${userUuid}/documents`,
    { json: body },
  );
}

/**
 * `DELETE /contractors/{uuid}/personnel/{userUuid}/documents/{linkUuid}` (`update`) — belge
 * linkini **soft-delete** eder. Yok/kapsam dışı → `404 PERSONNEL_DOCUMENT_NOT_FOUND` (taşeron/
 * personel yok → `404 CONTRACTOR_NOT_FOUND`/`PERSONNEL_NOT_FOUND`).
 *
 * ⚠️ Silme hedefi **link kaydının kendi `uuid`'idir** (`PersonnelDocumentDto.uuid` ← entity.Uuid),
 * §8 storage `documentUuid`'i **DEĞİL** (backend `d => d.Uuid == …` ile çözer; ikisi ayrı GUID).
 * §8 `documentUuid` gönderilirse her silme `404 PERSONNEL_DOCUMENT_NOT_FOUND` döner. İndirme ise
 * §8 presigned GET ile `documentUuid` üzerinden yapılır.
 */
export function deletePersonnelDocument(
  contractorUuid: string,
  userUuid: string,
  linkUuid: string,
) {
  return api.delete<Empty>(
    `contractors/${contractorUuid}/personnel/${userUuid}/documents/${linkUuid}`,
  );
}

// ============================================================================
// 15.4 Belge onayı (İSG uzmanı — yalnız yol A), `[ModuleAction("contractors", "update")]`
// ============================================================================
//
// KAYNAK: api-contract.md §15.4. Onay/red **yalnız ana firma (web-expert)** yolundadır; yol B
// (supplier yetkili) + yol C (personel-self) belgeyi **yükler**, İSG uzmanı **onaylar/reddeder**.
// Her ikisi de **idempotent**tir (yeniden-set; onay önceki red gerekçesini temizler) ve güncel
// belgeyi (`approvalStatus`/`approvedByName`/`approvedAt`) döner. Bu onay durumu giriş izni
// kapısının (§17.2/§22 tip-bazlı `requiredDocumentsSatisfied`/`missingDocumentTypes`) belge boyutu
// girdisidir — başarı sonrası izin detayı (readiness) **da** invalidate edilmelidir (çağıran katman).

/**
 * `POST /contractors/{uuid}/personnel/{userUuid}/documents/{linkUuid}/approve` (`update`) —
 * belgeyi **onaylar** (İSG uzmanı; §15.4). `approvalStatus` → `Approved`; `approvedByUserId`/
 * `approvedAt` set; önceki `rejectionReason` temizlenir. **İdempotent** (zaten onaylı yeniden
 * onaylanabilir). Taşeron/personel/belge yok/kapsam dışı → ilgili `404`. → güncel belge.
 */
export function approvePersonnelDocument(
  contractorUuid: string,
  userUuid: string,
  linkUuid: string,
) {
  return api.post<PersonnelDocumentDto>(
    `contractors/${contractorUuid}/personnel/${userUuid}/documents/${linkUuid}/approve`,
  );
}

/**
 * `POST /contractors/{uuid}/personnel/{userUuid}/documents/{linkUuid}/reject` (`update`) —
 * belgeyi **reddeder** (İSG uzmanı; §15.4). `approvalStatus` → `Rejected`; `rejectionReason`/
 * `approvedByUserId`/`approvedAt` set. `reason` zorunlu (boş → `400 VALIDATION_FAILED`).
 * **İdempotent**. Taşeron/personel/belge yok/kapsam dışı → ilgili `404`. → güncel belge.
 */
export function rejectPersonnelDocument(
  contractorUuid: string,
  userUuid: string,
  linkUuid: string,
  body: PersonnelDocumentRejectRequest,
) {
  return api.post<PersonnelDocumentDto>(
    `contractors/${contractorUuid}/personnel/${userUuid}/documents/${linkUuid}/reject`,
    { json: body },
  );
}
